import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AuthContext } from "../../../context/authcontext";
import { setHeaderCollapse } from "../../../core/data/redux/commonSlice";
import { getLifecycleLabels, LifecycleLabels } from "../../../utils/lifecycleUtils";
import { useSalesUserId } from "../../../utils/salesUserScope";
import { HeaderAction, ExportDropdown, ExportKind, useTemplateFont } from "../Dashboard/common";
import { GranularityToggle } from "../Dashboard/chartControls";
import { downloadExportFile } from "../Dashboard/dashboardApi";
import { exportDashboardPdf } from "../Dashboard/exportDashboardPdf";
import { DEFAULT_DATE_RANGE_LABEL, defaultDateRange } from "../Dashboard/filters/DashboardFilterDropdown";
import SummaryFilterDropdown, { SummaryFilterChips } from "./summaryReport/SummaryFilterDropdown";
import {
  SummaryFilterState,
  emptySummaryFilters,
  formatSummaryDateRange,
  getSummaryFilterChips,
  toSummaryQuery,
} from "./summaryReport/summaryFilters";
import {
  SummaryEntityKey,
  SummaryPeriod,
  SummaryReport,
  fetchSummaryReport,
  exportSummaryReportExcel,
} from "./summaryReport/summaryReportService";
import {
  ChartSkeleton,
  DropdownOption,
  MetricToggle,
  OptionDropdown,
  ProductBarChart,
  StageFunnelChart,
  SummaryCard,
  SummaryMetric,
  TimelineChart,
  TopEntitiesTable,
} from "./summaryReport/SummaryCards";
import "./summaryReport/summaryReport.css";

const SUPER_ADMIN_ROLES = ["superadmin", "super_admin", "platform_analyst", "platformanalyst", "apiadmin", "api_admin"];

const PERIOD_NOUNS: Record<SummaryPeriod, string> = {
  by_week: "Week",
  by_month: "Month",
  by_year: "Year",
};

type MetricChart = "stages" | "products" | "timeline";

const METRIC_STORAGE_KEY = "analytics_summary_chart_metrics";

const DEFAULT_CHART_METRICS: Record<MetricChart, SummaryMetric> = {
  stages: "count",
  products: "count",
  timeline: "count",
};

const readStoredMetrics = (): Record<MetricChart, SummaryMetric> => {
  try {
    const stored = JSON.parse(localStorage.getItem(METRIC_STORAGE_KEY) || "{}");
    return (Object.keys(DEFAULT_CHART_METRICS) as MetricChart[]).reduce(
      (result, chart) => ({ ...result, [chart]: stored?.[chart] === "amount" ? "amount" : "count" }),
      { ...DEFAULT_CHART_METRICS }
    );
  } catch {
    return { ...DEFAULT_CHART_METRICS };
  }
};

const LIFECYCLE_ENTITIES: Record<string, SummaryEntityKey> = {
  LEAD: "leads",
  PIPELINE: "pipelines",
  DEAL: "deals",
};

const pluralOf = (key: SummaryEntityKey, names: LifecycleLabels) =>
  key === "deals" ? names.deals : key === "pipelines" ? names.pipelines : names.leads;

const Analytics = () => {
  useTemplateFont();
  const dispatch = useDispatch();
  const headerCollapse = useSelector((state: any) => state.commonSlice.headerCollapse);
  const { auth, selectedTenantId, setSelectedTenantId, lifecycleTypeNames } = useContext(AuthContext);
  const names = useMemo(() => getLifecycleLabels(lifecycleTypeNames), [lifecycleTypeNames]);
  const salesUserId = useSalesUserId();

  const [filters, setFilters] = useState<SummaryFilterState>(emptySummaryFilters);
  const [data, setData] = useState<SummaryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [topEntity, setTopEntity] = useState<SummaryEntityKey>("leads");
  const [stageEntity, setStageEntity] = useState<SummaryEntityKey>("leads");
  const [productEntity, setProductEntity] = useState<SummaryEntityKey>("leads");
  const [timelineEntity, setTimelineEntity] = useState<SummaryEntityKey>("leads");
  const [timelinePeriod, setTimelinePeriod] = useState<SummaryPeriod>("by_month");
  const [chartMetrics, setChartMetrics] = useState<Record<MetricChart, SummaryMetric>>(readStoredMetrics);

  const changeMetric = (chart: MetricChart) => (next: SummaryMetric) => {
    setChartMetrics((prev) => {
      const updated = { ...prev, [chart]: next };
      try {
        localStorage.setItem(METRIC_STORAGE_KEY, JSON.stringify(updated));
      } catch {}
      return updated;
    });
  };

  const isSuperAdmin = useMemo(
    () =>
      (auth?.tenants ?? []).some((tenant: any) =>
        [tenant?.role_key, tenant?.role_name].some((role) => SUPER_ADMIN_ROLES.includes(String(role ?? "").toLowerCase()))
      ),
    [auth]
  );

  useEffect(() => {
    const readTenantId = () => localStorage.getItem("selectedTenantId") || localStorage.getItem("tenant_id") || "";
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "selectedTenantId" || event.key === "tenant_id") {
        setSelectedTenantId(readTenantId() || null);
      }
    };
    window.addEventListener("storage", handleStorage);
    let lastTenantId = selectedTenantId;
    const interval = setInterval(() => {
      const currentTenantId = readTenantId();
      if (isSuperAdmin && !selectedTenantId && !currentTenantId) return;
      if (currentTenantId !== lastTenantId) {
        setSelectedTenantId(currentTenantId || null);
        lastTenantId = currentTenantId;
      }
    }, 1000);
    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, [selectedTenantId, isSuperAdmin, setSelectedTenantId]);

  useEffect(() => {
    setData(null);
    setFilters(emptySummaryFilters);
  }, [selectedTenantId]);

  const primaryLifecycle = filters.lifecycles[0]?.value ?? null;

  useEffect(() => {
    const entity = primaryLifecycle ? LIFECYCLE_ENTITIES[primaryLifecycle] : undefined;
    if (!entity) return;
    setTopEntity(entity);
    setStageEntity(entity);
    setProductEntity(entity);
    setTimelineEntity(entity);
  }, [primaryLifecycle]);

  const query = useMemo(() => toSummaryQuery(filters, salesUserId), [filters, salesUserId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchSummaryReport(selectedTenantId, query, controller.signal)
      .then((response) => setData(response))
      .catch((err) => {
        if (!controller.signal.aborted) setError(err?.message || "Failed to load analytics");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [selectedTenantId, query, reloadKey]);

  const reload = () => setReloadKey((key) => key + 1);

  const handleExport = async (kind: ExportKind) => {
    const container = reportRef.current;
    if (!selectedTenantId || !data || exporting) return;
    setExporting(kind);
    try {
      if (kind === "pdf") {
        if (!container) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        const chips = getSummaryFilterChips(filters).map((chip) => chip.label);
        if (!filters.startDate || !filters.endDate) {
          const range = defaultDateRange();
          chips.push(`${DEFAULT_DATE_RANGE_LABEL}: ${formatSummaryDateRange(range.startDate, range.endDate)}`);
        }
        await exportDashboardPdf({
          container,
          sectionSelector: ".sr-section",
          title: "Analytics",
          filtersText: chips.length ? chips.join(" · ") : "No filters applied",
          fileName: `analytics-summary-${dayjs().format("YYYY-MM-DD")}.pdf`,
        });
      } else {
        const file = await exportSummaryReportExcel(selectedTenantId, query);
        await downloadExportFile(file, `analytics-summary-${dayjs().format("YYYY-MM-DD")}`);
      }
    } catch (err) {
      setError((err as Error)?.message || `Failed to export ${kind === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting(null);
    }
  };

  const entityOptions = useMemo<DropdownOption<SummaryEntityKey>[]>(
    () => [
      { value: "leads", label: names.leads },
      { value: "pipelines", label: names.pipelines },
      { value: "deals", label: names.deals },
    ],
    [names]
  );

  const currency = filters.currencies.length === 1 ? filters.currencies[0].value : null;
  const loaded = loading && !data ? null : data;

  const header = (
    <>
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
        <h4 className="mb-0">Analytics</h4>
        <div className="gap-2 d-flex align-items-center flex-wrap">
          {selectedTenantId && (
            <SummaryFilterDropdown
              filters={filters}
              onChange={setFilters}
              names={names}
              hideUsers={salesUserId !== null}
              loading={loading}
            />
          )}
          <ExportDropdown
            disabled={!data || loading}
            exporting={exporting}
            spinClassName="sr-spin"
            onExport={handleExport}
          />
          <HeaderAction label="Refresh" icon={`ti ti-refresh${loading ? " sr-spin" : ""}`} onClick={reload} />
          <HeaderAction
            label={headerCollapse ? "Expand" : "Collapse"}
            icon="ti ti-transition-top"
            onClick={() => dispatch(setHeaderCollapse(!headerCollapse))}
          />
        </div>
      </div>
      <SummaryFilterChips filters={filters} onChange={setFilters} disabled={loading} showDefaultDateRange />
    </>
  );

  if (!selectedTenantId) {
    return (
      <div className="page-wrapper summary-report">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-building-community fs-1 text-muted mb-3" />
              <h5>{isSuperAdmin ? "Select a Tenant First" : "Select a Tenant"}</h5>
              <p className="text-muted mb-0">Please select a tenant to view analytics data.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data && error && !loading) {
    return (
      <div className="page-wrapper summary-report">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-alert-circle fs-1 text-danger mb-3" />
              <h5>Unable to load analytics</h5>
              <p className="text-muted mb-3">{error}</p>
              <button type="button" className="btn btn-outline-light" onClick={reload}>
                <i className="ti ti-refresh me-1" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const topLabel = pluralOf(topEntity, names);
  const stageLabel = pluralOf(stageEntity, names);
  const productLabel = pluralOf(productEntity, names);
  const timelineLabel = pluralOf(timelineEntity, names);

  return (
    <div className="page-wrapper summary-report">
      <div className={`content pb-0${loading && data ? " sr-refreshing" : ""}`}>
        {header}

        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-4">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={reload}>
              Try Again
            </button>
          </div>
        )}

        <div ref={reportRef}>
          <div className="row sr-section" aria-busy={!loaded}>
            <div className="col-12 d-flex">
              <SummaryCard
                title={`Top 5 ${topLabel} by Estimated Values`}
                icon="ti ti-trophy"
                actions={<OptionDropdown value={topEntity} options={entityOptions} onChange={setTopEntity} />}
              >
                {loaded ? (
                  <TopEntitiesTable
                    entityKey={topEntity}
                    items={loaded.top[topEntity]}
                    currency={currency}
                    emptyMessage={`No ${topLabel.toLowerCase()} found for the selected filters`}
                  />
                ) : (
                  <ChartSkeleton height={260} variant="rows" />
                )}
              </SummaryCard>
            </div>
          </div>

          <div className="row sr-section">
            <div className="col-xl-6 d-flex">
              <SummaryCard
                title={`${stageLabel} by Stages`}
                icon="ti ti-chart-histogram"
                actions={
                  <>
                    <MetricToggle value={chartMetrics.stages} onChange={changeMetric("stages")} />
                    <OptionDropdown value={stageEntity} options={entityOptions} onChange={setStageEntity} />
                  </>
                }
              >
                {loaded ? (
                  <StageFunnelChart
                    stages={loaded.stages[stageEntity]}
                    metric={chartMetrics.stages}
                    currency={currency}
                    emptyMessage="No data available for the selected filters"
                  />
                ) : (
                  <ChartSkeleton height={400} />
                )}
              </SummaryCard>
            </div>
            <div className="col-xl-6 d-flex">
              <SummaryCard
                title={`${productLabel} by Product & Service`}
                icon="ti ti-chart-bar"
                actions={
                  <>
                    <MetricToggle value={chartMetrics.products} onChange={changeMetric("products")} />
                    <OptionDropdown value={productEntity} options={entityOptions} onChange={setProductEntity} />
                  </>
                }
              >
                {loaded ? (
                  <ProductBarChart
                    products={loaded.products[productEntity]}
                    seriesName={productLabel}
                    metric={chartMetrics.products}
                    currency={currency}
                    emptyMessage="No product/service data available"
                  />
                ) : (
                  <ChartSkeleton height={380} />
                )}
              </SummaryCard>
            </div>
          </div>

          <div className="row sr-section">
            <div className="col-12 d-flex">
              <SummaryCard
                title={`${timelineLabel} by ${PERIOD_NOUNS[timelinePeriod]}`}
                icon="ti ti-chart-line"
                actions={<OptionDropdown value={timelineEntity} options={entityOptions} onChange={setTimelineEntity} />}
                toolbar={
                  <>
                    <MetricToggle
                      label="Show"
                      value={chartMetrics.timeline}
                      onChange={changeMetric("timeline")}
                    />
                    <GranularityToggle value={timelinePeriod} onChange={setTimelinePeriod} />
                  </>
                }
              >
                {loaded ? (
                  <TimelineChart
                    points={loaded.timeline[timelinePeriod][timelineEntity]}
                    period={timelinePeriod}
                    seriesName={timelineLabel}
                    metric={chartMetrics.timeline}
                    currency={currency}
                    emptyMessage="No data available for the selected filters"
                  />
                ) : (
                  <ChartSkeleton height={300} />
                )}
              </SummaryCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
